import type { FundingApplicationSchemas } from "api-types";
import type {
  BankAccountGroupConnectionMemberSchema,
  BankAccountGroupSchema,
  BankAccount,
  BankConnectionProviderType,
  BankConnectionSchema,
} from "bankconnect";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import type { IntlShape, MessageDescriptor } from "react-intl";
import { MAX_DATE_NO_CSV } from "shared-ui";

import { MESSAGES } from "./message-definitions";

export type SelectedAccount = {
  connection: BankConnectionSchema;
  account: BankAccount;
};

const PROVIDER_LABELS: Record<BankConnectionProviderType, string> = {
  plaid: "Plaid",
  basiq: "Basiq",
  truelayer: "TrueLayer",
  wayflyer: "Wayflyer",
  mocked: "Mocked",
};

export const capitalize = (value: string) => {
  const [first, ...rest] = value;
  return first === undefined ? value : first.toUpperCase() + rest.join("").toLowerCase();
};

export const capitalizeFirstLetter = (value: string) => {
  const [first, ...rest] = value;
  return first === undefined ? value : first.toUpperCase() + rest.join("");
};

export const formatInstitutionLabel = (
  intl: IntlShape,
  institutionName: string | null,
  mask: string | null
): string => {
  const institutionLabel = institutionName || intl.formatMessage(MESSAGES.businessAccountFallback);
  return mask ? `${institutionLabel} ··${mask}` : institutionLabel;
};

export const getAccountLabel = (intl: IntlShape, connection: BankConnectionSchema, account: BankAccount): string =>
  formatInstitutionLabel(intl, connection.institutionName, account.mask);

export const getConnectedSubtitle = (intl: IntlShape, connection: BankConnectionSchema, account: BankAccount): string =>
  account.currency
    ? intl.formatMessage(MESSAGES.connectedWithProvider, {
        provider: PROVIDER_LABELS[connection.providerType],
        currency: account.currency,
      })
    : intl.formatMessage(MESSAGES.connectedWithProviderNoCurrency, {
        provider: PROVIDER_LABELS[connection.providerType],
      });

export const getGroupAccountLabel = (intl: IntlShape, group: BankAccountGroupSchema): string =>
  formatInstitutionLabel(intl, group.institutionName, group.mask);

export const findGroupByUuid = (
  accountGroups: BankAccountGroupSchema[],
  uuid: string
): BankAccountGroupSchema | undefined => accountGroups.find((candidate) => candidate.uuid === uuid);

export const hasBankAccounts = (
  accountGroups: BankAccountGroupSchema[],
  ungroupedConnections: BankConnectionSchema[]
): boolean =>
  accountGroups.length > 0 ||
  ungroupedConnections.some((connection) => connection.accounts.some((account) => account.pull_enabled));

const getGroupProvidersLabel = (connectionMembers: BankAccountGroupConnectionMemberSchema[]): string =>
  [...new Set(connectionMembers.map((member) => PROVIDER_LABELS[member.connection.providerType]))].join(", ");

export const getGroupSubtitle = (intl: IntlShape, group: BankAccountGroupSchema): string => {
  const hasStatements = group.statementMembers.length > 0;

  if (group.connectionMembers.length > 0) {
    const provider = getGroupProvidersLabel(group.connectionMembers);
    if (!hasStatements) {
      return group.currency
        ? intl.formatMessage(MESSAGES.connectedWithProvider, { provider, currency: group.currency })
        : intl.formatMessage(MESSAGES.connectedWithProviderNoCurrency, { provider });
    }
    return group.currency
      ? intl.formatMessage(MESSAGES.connectedWithProviderAndStatements, { provider, currency: group.currency })
      : intl.formatMessage(MESSAGES.connectedWithProviderAndStatementsNoCurrency, { provider });
  }

  return group.currency
    ? intl.formatMessage(MESSAGES.addedWithStatements, { currency: group.currency })
    : intl.formatMessage(MESSAGES.addedWithStatementsNoCurrency);
};

type DateRange = { start: Dayjs; end: Dayjs };
type Interval = { start: number; end: number };

type StatementSource = Pick<BankAccountGroupSchema, "statementMembers">;

const statementDateRanges = (group: StatementSource): DateRange[] =>
  group.statementMembers.flatMap((member) =>
    member.account?.statementStartDate && member.account.statementEndDate
      ? [{ start: dayjs(member.account.statementStartDate), end: dayjs(member.account.statementEndDate) }]
      : []
  );

export type MonthCoverageFill =
  | "none"
  | "half-start"
  | "half-end"
  | "full"
  | "reviewed"
  | "half-start-reviewed"
  | "half-end-reviewed";

export type MonthCoverage = {
  // Short form for the bar's tick labels; fullLabel is for prose, where "since Aug" reads wrong.
  label: string;
  fullLabel: string;
  fill: MonthCoverageFill;
};

const mergeIntervals = (intervals: Interval[]): Interval[] => {
  const merged: Interval[] = [];
  for (const interval of [...intervals].sort((a, b) => a.start - b.start)) {
    const last = merged[merged.length - 1];
    if (last && interval.start <= last.end + 1) {
      last.end = Math.max(last.end, interval.end);
    } else {
      merged.push(interval);
    }
  }
  return merged;
};

const clipRangesToWindow = (ranges: DateRange[], windowStart: number, windowEnd: number): Interval[] =>
  ranges
    .map((range) => ({
      start: Math.max(range.start.startOf("day").valueOf(), windowStart),
      end: Math.min(range.end.endOf("day").valueOf(), windowEnd),
    }))
    .filter((interval) => interval.start <= interval.end);

const getMonthFill = (ranges: DateRange[], monthStart: Dayjs, monthEnd: Dayjs): MonthCoverageFill => {
  const clipped = clipRangesToWindow(ranges, monthStart.valueOf(), monthEnd.valueOf());

  if (clipped.length === 0) {
    return "none";
  }

  const merged = mergeIntervals(clipped);
  const monthDuration = monthEnd.valueOf() - monthStart.valueOf();
  const coveredDuration = merged.reduce((total, interval) => total + (interval.end - interval.start), 0);

  if (coveredDuration === monthDuration) {
    return "full";
  }

  const midpoint = (monthStart.valueOf() + monthEnd.valueOf()) / 2;
  const coveredBeforeMidpoint = merged.reduce(
    (total, interval) => total + Math.max(0, Math.min(interval.end, midpoint) - interval.start),
    0
  );
  return coveredBeforeMidpoint >= coveredDuration - coveredBeforeMidpoint ? "half-start" : "half-end";
};

const requiredWindowEnd = (now: Dayjs): Dayjs => now.subtract(1, "day").endOf("day");

// Matches the same cutoff the AIR upload flow uses (MAX_DATE_NO_CSV), due on the day itself. Only
// affects copy wording (see getStatementCoverageBodyText) - whether a gap exists at all is governed
// purely by the 14-day rule below, for every month alike.
const isMonthNotYetDue = (monthStart: Dayjs, now: Dayjs): boolean =>
  now.isSame(monthStart, "month") && now.date() < MAX_DATE_NO_CSV;

// True only on a month's first day, when "yesterday" falls outside it entirely and there's nothing
// to evaluate yet. Excluded from classification so a real gap ending right before it still counts
// as trailing, not middle.
const isCurrentMonthWindowEmpty = (monthStart: Dayjs, now: Dayjs): boolean =>
  requiredWindowEnd(now).isBefore(monthStart);

// Independent of month-bucket boundaries - the exact days with no data at all.
const getUncoveredIntervals = (ranges: DateRange[], windowStart: number, windowEnd: number): Interval[] => {
  const covered = mergeIntervals(clipRangesToWindow(ranges, windowStart, windowEnd));

  const uncovered: Interval[] = [];
  let cursor = windowStart;
  for (const interval of covered) {
    if (interval.start > cursor) {
      uncovered.push({ start: cursor, end: interval.start - 1 });
    }
    cursor = Math.max(cursor, interval.end + 1);
  }
  if (cursor <= windowEnd) {
    uncovered.push({ start: cursor, end: windowEnd });
  }
  return uncovered;
};

// A gap under this length is tolerated everywhere - a brand-new month with no data yet, a stale
// tail on an otherwise-fine account, or a short hole in an old, closed month all read as fine.
const MIN_REPORTABLE_GAP_DAYS = 14;

// Whole calendar days, not a raw millisecond diff - otherwise a true 15-day gap can floor to 14
// depending on time-of-day (e.g. an interval ending at 23:59:59 vs. a midday "now").
const dayCount = (startMs: number, endMs: number): number =>
  dayjs(endMs).startOf("day").diff(dayjs(startMs).startOf("day"), "day") + 1;

const getReportableGaps = (ranges: DateRange[], windowStart: number, windowEnd: number): Interval[] =>
  getUncoveredIntervals(ranges, windowStart, windowEnd).filter(
    (interval) => dayCount(interval.start, interval.end) >= MIN_REPORTABLE_GAP_DAYS
  );

const overlapsAnyGap = (gaps: Interval[], rangeStart: number, rangeEnd: number): boolean =>
  gaps.some((gap) => gap.start <= rangeEnd && gap.end >= rangeStart);

// A month with no reportable gap touching it reads as fully covered - only once a real gap
// overlaps it do we bother computing the precise half/none shape.
const getToleratedFill = (
  ranges: DateRange[],
  monthStart: Dayjs,
  effectiveEnd: Dayjs,
  reportableGaps: Interval[]
): MonthCoverageFill =>
  overlapsAnyGap(reportableGaps, monthStart.valueOf(), effectiveEnd.valueOf())
    ? getMonthFill(ranges, monthStart, effectiveEnd)
    : "full";

// Shared with the exact gap-date lookup below so both agree on what a bucket spans.
const monthBounds = (now: Dayjs, requiredStatementMonths: number, index: number): { start: Dayjs; end: Dayjs } => {
  const month = now.subtract(requiredStatementMonths - 1 - index, "month");
  return { start: month.startOf("month"), end: month.endOf("month") };
};

type MonthLabel = { label: string; fullLabel: string; monthStart: Dayjs; monthEnd: Dayjs };

const getMonthLabels = (requiredStatementMonths: number, now: Dayjs, locale: string): MonthLabel[] => {
  const shortMonthFormatter = new Intl.DateTimeFormat(locale, { month: "short" });
  const fullMonthFormatter = new Intl.DateTimeFormat(locale, { month: "long" });

  return Array.from({ length: requiredStatementMonths }, (_, index) => {
    const { start: monthStart, end: monthEnd } = monthBounds(now, requiredStatementMonths, index);
    const monthDate = monthStart.toDate();
    return {
      label: shortMonthFormatter.format(monthDate),
      fullLabel: fullMonthFormatter.format(monthDate),
      monthStart,
      monthEnd,
    };
  });
};

export const getMonthCoverage = (
  group: StatementSource,
  requiredStatementMonths: number,
  now: Dayjs = dayjs(),
  locale = "en"
): MonthCoverage[] => {
  const ranges = statementDateRanges(group);
  const windowStart = monthBounds(now, requiredStatementMonths, 0).start.valueOf();
  const windowEnd = requiredWindowEnd(now);
  const reportableGaps = getReportableGaps(ranges, windowStart, windowEnd.valueOf());

  return getMonthLabels(requiredStatementMonths, now, locale).map(({ label, fullLabel, monthStart, monthEnd }) => {
    const effectiveEnd = windowEnd.isBefore(monthEnd) ? windowEnd : monthEnd;
    return {
      label,
      fullLabel,
      fill: effectiveEnd.isBefore(monthStart)
        ? "full" // nothing in this month is due to have happened yet (e.g. its 1st day)
        : getToleratedFill(ranges, monthStart, effectiveEnd, reportableGaps),
    };
  });
};

export type MonthRun = { start: number; end: number };

const missingMonthRuns = (months: MonthCoverage[]): MonthRun[] => {
  const runs: MonthRun[] = [];
  let runStart: number | null = null;
  months.forEach((month, index) => {
    if (month.fill !== "full") {
      runStart ??= index;
      return;
    }
    if (runStart !== null) {
      runs.push({ start: runStart, end: index - 1 });
      runStart = null;
    }
  });
  if (runStart !== null) {
    runs.push({ start: runStart, end: months.length - 1 });
  }
  return runs;
};

// Indices, not resolved dates - getStatementCoverageBodyText resolves them against the raw
// statement ranges for the exact days bounding each gap.
export type CoverageGaps =
  | { kind: "complete" }
  | { kind: "none" }
  | { kind: "leading"; gapEnd: number }
  | { kind: "trailing"; gapStart: number }
  | { kind: "middle"; gapStart: number; gapEnd: number }
  | { kind: "leadingAndTrailing"; leadingGapEnd: number; trailingGapStart: number }
  | { kind: "fragmented"; runs: MonthRun[] };

export const classifyCoverageGaps = (months: MonthCoverage[], effectiveLastIndex = months.length - 1): CoverageGaps => {
  const gaps = missingMonthRuns(months);

  if (gaps.length === 0) {
    return { kind: "complete" };
  }

  if (gaps.length === 1) {
    const [gap] = gaps;
    const touchesStart = gap.start === 0;
    const touchesEnd = gap.end === effectiveLastIndex;
    if (touchesStart && touchesEnd) {
      return { kind: "none" };
    }
    if (touchesStart) {
      return { kind: "leading", gapEnd: gap.end };
    }
    if (touchesEnd) {
      return { kind: "trailing", gapStart: gap.start };
    }
    return { kind: "middle", gapStart: gap.start, gapEnd: gap.end };
  }

  if (gaps.length === 2) {
    const [leading, trailing] = gaps;
    if (leading.start === 0 && trailing.end === effectiveLastIndex) {
      return { kind: "leadingAndTrailing", leadingGapEnd: leading.end, trailingGapStart: trailing.start };
    }
  }

  return { kind: "fragmented", runs: gaps };
};

// Ocrolus never reads a CSV export, so it's only worth accepting one for a freshness (trailing)
// issue - a leading/middle gap needs a real statement regardless. A trailing gap can occur before
// the cutoff too (an already-due earlier month stale while the current one is merely not due yet),
// so the cutoff needs its own check here rather than being implied by "trailing" alone.
export const isCsvEligibleGap = (
  group: BankAccountGroupSchema,
  requiredStatementMonths: number,
  now: Dayjs = dayjs()
): boolean => {
  const months = getMonthCoverage(group, requiredStatementMonths, now);
  const lastMonthStart = monthBounds(now, requiredStatementMonths, months.length - 1).start;
  if (isMonthNotYetDue(lastMonthStart, now)) {
    return false;
  }
  const gaps = classifyCoverageGaps(months);
  // A single required month that's entirely missing classifies as "none", not "trailing" - but
  // when that one month is the current month, it's the same case.
  return gaps.kind === "trailing" || (gaps.kind === "none" && months.length === 1);
};

export const hasSufficientStatementCoverage = (
  group: BankAccountGroupSchema,
  requiredStatementMonths: number,
  now: Dayjs = dayjs()
): boolean => classifyCoverageGaps(getMonthCoverage(group, requiredStatementMonths, now)).kind === "complete";

export type AccountReadinessBadge = {
  badgeStatus: "positive" | "warning" | "info";
  badgeText: string;
};

export const hasDisconnectedConnectionMember = (group: BankAccountGroupSchema): boolean =>
  group.connectionMembers.some((member) => !member.connection.accessible);

export type AccountReviewState = "ready" | "to_review" | "reviewed";

export const hasStatementCoverageGap = (
  group: BankAccountGroupSchema,
  requiredStatementMonths: number,
  now: Dayjs = dayjs()
): boolean =>
  group.connectionMembers.length === 0 && !hasSufficientStatementCoverage(group, requiredStatementMonths, now);

export const getAccountReviewState = (
  group: BankAccountGroupSchema,
  requiredStatementMonths: number,
  now: Dayjs = dayjs()
): AccountReviewState => {
  const hasGap = hasStatementCoverageGap(group, requiredStatementMonths, now);
  if (hasGap) {
    return group.isIncompleteCoverageAcknowledged ? "reviewed" : "to_review";
  }
  return hasDisconnectedConnectionMember(group) ? "to_review" : "ready";
};

type AccountsToReviewInput = {
  accountGroups: BankAccountGroupSchema[];
  ungroupedConnections: BankConnectionSchema[];
  ungroupedStatements: unknown[];
  requiredStatementMonths: number;
  now?: Dayjs;
};

export const countGroupsToReview = (
  accountGroups: BankAccountGroupSchema[],
  requiredStatementMonths: number,
  now: Dayjs = dayjs()
): number =>
  accountGroups.filter((group) => getAccountReviewState(group, requiredStatementMonths, now) === "to_review").length;

export const hasAccountsToReview = ({
  accountGroups,
  ungroupedConnections,
  ungroupedStatements,
  requiredStatementMonths,
  now = dayjs(),
}: AccountsToReviewInput): boolean =>
  ungroupedConnections.length > 0 ||
  ungroupedStatements.length > 0 ||
  countGroupsToReview(accountGroups, requiredStatementMonths, now) > 0;

const ACCOUNT_BADGES: Record<
  AccountReviewState,
  { badgeStatus: AccountReadinessBadge["badgeStatus"]; message: MessageDescriptor }
> = {
  ready: { badgeStatus: "positive", message: MESSAGES.readyBadge },
  to_review: { badgeStatus: "warning", message: MESSAGES.toReviewBadge },
  reviewed: { badgeStatus: "info", message: MESSAGES.reviewedBadge },
};

export const getAccountReadinessBadge = (
  intl: IntlShape,
  group: BankAccountGroupSchema,
  requiredStatementMonths: number,
  now: Dayjs = dayjs()
): AccountReadinessBadge => {
  const { badgeStatus, message } = ACCOUNT_BADGES[getAccountReviewState(group, requiredStatementMonths, now)];
  return { badgeStatus, badgeText: intl.formatMessage(message) };
};

// A run's month-bucket boundaries only say which calendar months it touches; e.g. a run spanning
// May-June where data resumes June 29th should report the gap ending June 28th, not June 30th.
const getExactGapRanges = (uncoveredIntervals: Interval[], spanStart: number, spanEnd: number): DateRange[] => {
  const overlapping = uncoveredIntervals
    .filter((interval) => interval.end >= spanStart && interval.start <= spanEnd)
    .map((interval) => ({
      start: dayjs(Math.max(interval.start, spanStart)),
      end: dayjs(Math.min(interval.end, spanEnd)),
    }));
  return overlapping.length > 0 ? overlapping : [{ start: dayjs(spanStart), end: dayjs(spanEnd) }];
};

// Built manually rather than via Intl's own ordering, so the "day month year" arrangement stays
// fixed regardless of locale while the month name itself is still translated.
const formatGapDate = (date: Dayjs, locale: string): string => {
  const month = new Intl.DateTimeFormat(locale, { month: "long" }).format(date.toDate());
  return `${date.date()} ${month} ${date.year()}`;
};

export type StatementCoverageBodyText =
  | { kind: "text"; text: string }
  | { kind: "fragmentedList"; intro: string; periods: string[]; outro: string };

export const getCompleteCoverageBodyText = (intl: IntlShape): StatementCoverageBodyText => ({
  kind: "text",
  text: intl.formatMessage(MESSAGES.statementCoverageComplete),
});

const getRunGapRangesResolver = (
  group: StatementSource,
  requiredStatementMonths: number,
  now: Dayjs
): ((run: MonthRun) => DateRange[]) => {
  const windowStart = monthBounds(now, requiredStatementMonths, 0).start.valueOf();
  const windowEnd = requiredWindowEnd(now).valueOf();
  const reportableGaps = getReportableGaps(statementDateRanges(group), windowStart, windowEnd);
  // A run's month-bucket span can still hide more than one reportable gap - an edge month buckets
  // as "half" rather than "full" even with data on both sides of an internal gap. Sourced from
  // reportableGaps, not the raw uncovered days, so a sub-14-day interruption never gets its own period.
  return (run) => {
    const spanStart = monthBounds(now, requiredStatementMonths, run.start).start.valueOf();
    const spanEnd = Math.min(monthBounds(now, requiredStatementMonths, run.end).end.valueOf(), windowEnd);
    return getExactGapRanges(reportableGaps, spanStart, spanEnd);
  };
};

const formatGapPeriod = (intl: IntlShape, { start, end }: DateRange): string =>
  start.isSame(end, "day")
    ? formatGapDate(start, intl.locale)
    : intl.formatMessage(MESSAGES.statementCoverageDates, {
        startDate: formatGapDate(start, intl.locale),
        endDate: formatGapDate(end, intl.locale),
      });

export const getStatementCoverageBodyText = (
  intl: IntlShape,
  group: StatementSource,
  requiredStatementMonths: number,
  now: Dayjs = dayjs()
): StatementCoverageBodyText => {
  const months = getMonthCoverage(group, requiredStatementMonths, now, intl.locale);
  const lastMonthStart = monthBounds(now, requiredStatementMonths, months.length - 1).start;
  // Gates which copy wording applies once there's a trailing gap (see the "trailing" case below).
  const currentMonthNotYetDue = isMonthNotYetDue(lastMonthStart, now);
  // Classification itself only needs the narrower day-1 exclusion - see isCurrentMonthWindowEmpty.
  const effectiveLastIndex = isCurrentMonthWindowEmpty(lastMonthStart, now) ? months.length - 2 : months.length - 1;
  const gaps = classifyCoverageGaps(months, effectiveLastIndex);
  const formatDate = (date: Dayjs) => formatGapDate(date, intl.locale);
  const formatPeriod = (range: DateRange) => formatGapPeriod(intl, range);
  const fragmentedListFrom = (ranges: DateRange[]): StatementCoverageBodyText => {
    const periods = ranges.map(formatPeriod);
    return {
      kind: "fragmentedList",
      intro: intl.formatMessage(MESSAGES.statementCoverageFragmentedGapsIntro, { count: periods.length }),
      periods,
      outro: intl.formatMessage(MESSAGES.statementCoverageFragmentedGapsOutro),
    };
  };
  const exactRangesForRun = getRunGapRangesResolver(group, requiredStatementMonths, now);

  const textOrFragmented = (ranges: DateRange[], buildText: (range: DateRange) => string): StatementCoverageBodyText =>
    ranges.length > 1 ? fragmentedListFrom(ranges) : { kind: "text", text: buildText(ranges[0]) };

  switch (gaps.kind) {
    case "complete":
      return getCompleteCoverageBodyText(intl);
    case "none":
      // A single required month that's entirely missing is the current-month-only case too - see
      // isCsvEligibleGap.
      if (months.length === 1) {
        return {
          kind: "text",
          text: intl.formatMessage(MESSAGES.statementCoverageCurrentMonthGap, {
            date: formatDate(monthBounds(now, requiredStatementMonths, 0).start),
          }),
        };
      }
      return { kind: "text", text: intl.formatMessage(MESSAGES.statementCoverageNone) };
    case "leading":
      return textOrFragmented(exactRangesForRun({ start: 0, end: gaps.gapEnd }), ({ end }) =>
        intl.formatMessage(MESSAGES.statementCoverageLeadingGap, { date: formatDate(end.add(1, "day")) })
      );
    case "trailing":
      // Before the cutoff this can still be a stale, already-due earlier month rather than a
      // not-yet-available current-month PDF, so it keeps the plain copy in that case.
      return textOrFragmented(exactRangesForRun({ start: gaps.gapStart, end: months.length - 1 }), ({ start }) =>
        intl.formatMessage(
          currentMonthNotYetDue ? MESSAGES.statementCoverageTrailingGap : MESSAGES.statementCoverageCurrentMonthGap,
          { date: formatDate(start) }
        )
      );
    case "middle":
      return textOrFragmented(exactRangesForRun({ start: gaps.gapStart, end: gaps.gapEnd }), ({ start, end }) =>
        intl.formatMessage(MESSAGES.statementCoverageMiddleGap, {
          startDate: formatDate(start),
          endDate: formatDate(end),
        })
      );
    case "leadingAndTrailing": {
      const leadingRanges = exactRangesForRun({ start: 0, end: gaps.leadingGapEnd });
      const trailingRanges = exactRangesForRun({ start: gaps.trailingGapStart, end: months.length - 1 });
      if (leadingRanges.length > 1 || trailingRanges.length > 1) {
        return fragmentedListFrom([...leadingRanges, ...trailingRanges]);
      }
      const [{ end: leadingEnd }] = leadingRanges;
      const [{ start: trailingStart }] = trailingRanges;
      return {
        kind: "text",
        text: intl.formatMessage(MESSAGES.statementCoverageLeadingAndTrailingGap, {
          leadingDate: formatDate(leadingEnd.add(1, "day")),
          trailingDate: formatDate(trailingStart),
        }),
      };
    }
    case "fragmented":
      return fragmentedListFrom(gaps.runs.flatMap(exactRangesForRun));
  }
};

export const getConnectionMemberSubtitle = (intl: IntlShape, member: BankAccountGroupConnectionMemberSchema): string =>
  member.account.currency
    ? intl.formatMessage(MESSAGES.connectedWithProvider, {
        provider: PROVIDER_LABELS[member.connection.providerType],
        currency: member.account.currency,
      })
    : intl.formatMessage(MESSAGES.connectedWithProviderNoCurrency, {
        provider: PROVIDER_LABELS[member.connection.providerType],
      });

const countBankAccounts = (
  accountGroups: BankAccountGroupSchema[],
  ungroupedConnections: BankConnectionSchema[]
): number =>
  accountGroups.length +
  ungroupedConnections.reduce(
    (total, connection) => total + connection.accounts.filter((account) => account.pull_enabled).length,
    0
  );

const hasLiveConnection = (
  accountGroups: BankAccountGroupSchema[],
  ungroupedConnections: BankConnectionSchema[]
): boolean =>
  accountGroups.some((group) =>
    group.connectionMembers.some((member) => member.connection.accessible && member.account.pull_enabled)
  ) ||
  ungroupedConnections.some(
    (connection) => connection.accessible && connection.accounts.some((account) => account.pull_enabled)
  );

export type AggregateCoverage = {
  months: MonthCoverage[];
  body: StatementCoverageBodyText;
  kind: "connected" | "complete" | "gap";
};

export const getAggregateCoverage = (
  intl: IntlShape,
  accountGroups: BankAccountGroupSchema[],
  ungroupedConnections: BankConnectionSchema[],
  requiredStatementMonths: number,
  now: Dayjs = dayjs()
): AggregateCoverage => {
  const source: StatementSource = { statementMembers: accountGroups.flatMap((group) => group.statementMembers) };
  const months = getMonthCoverage(source, requiredStatementMonths, now, intl.locale);

  if (hasLiveConnection(accountGroups, ungroupedConnections)) {
    return {
      kind: "connected",
      months: months.map((month) => ({ ...month, fill: "full" })),
      body: {
        kind: "text",
        text: intl.formatMessage(MESSAGES.aggregateCoverageConnected, {
          count: countBankAccounts(accountGroups, ungroupedConnections),
        }),
      },
    };
  }

  if (classifyCoverageGaps(months).kind === "complete") {
    return {
      kind: "complete",
      months,
      body: {
        kind: "text",
        text: intl.formatMessage(MESSAGES.aggregateCoverageComplete, {
          count: countBankAccounts(accountGroups, ungroupedConnections),
        }),
      },
    };
  }

  return { kind: "gap", months, body: getStatementCoverageBodyText(intl, source, requiredStatementMonths, now) };
};

export const getGroupUuidsToAcknowledge = (
  accountGroups: BankAccountGroupSchema[],
  requiredStatementMonths: number,
  now: Dayjs = dayjs()
): string[] =>
  accountGroups
    .filter((group) => getAccountReviewState(group, requiredStatementMonths, now) === "to_review")
    .filter((group) => hasStatementCoverageGap(group, requiredStatementMonths, now))
    .map((group) => group.uuid);

export const getCoverageGapPeriods = (
  intl: IntlShape,
  group: BankAccountGroupSchema,
  requiredStatementMonths: number,
  now: Dayjs = dayjs()
): string[] => {
  const months = getMonthCoverage(group, requiredStatementMonths, now, intl.locale);
  const exactRangesForRun = getRunGapRangesResolver(group, requiredStatementMonths, now);
  return missingMonthRuns(months)
    .flatMap((run) => exactRangesForRun(run))
    .map((range) => formatGapPeriod(intl, range));
};

const REVIEWED_FILL: Record<MonthCoverageFill, MonthCoverageFill> = {
  none: "reviewed",
  "half-start": "half-start-reviewed",
  "half-end": "half-end-reviewed",
  full: "full",
  reviewed: "reviewed",
  "half-start-reviewed": "half-start-reviewed",
  "half-end-reviewed": "half-end-reviewed",
};

export const markGapMonthsReviewed = (months: MonthCoverage[]): MonthCoverage[] =>
  months.map((month) => ({ ...month, fill: REVIEWED_FILL[month.fill] }));

export const applyAccountsToReview = (
  intl: IntlShape,
  coverage: AggregateCoverage,
  accountsToReviewCount: number,
  requiredStatementMonths: number
): AggregateCoverage =>
  coverage.kind === "gap" || accountsToReviewCount === 0
    ? coverage
    : {
        ...coverage,
        body: {
          kind: "text",
          text: intl.formatMessage(MESSAGES.aggregateCoverageReviewAccounts, {
            requiredMonths: requiredStatementMonths,
            count: accountsToReviewCount,
          }),
        },
      };

export const getDisplayedCoverage = (
  intl: IntlShape,
  input: AccountsToReviewInput & { isProcessingUploads: boolean }
): AggregateCoverage => {
  const { accountGroups, ungroupedConnections, requiredStatementMonths, isProcessingUploads, now = dayjs() } = input;

  const aggregateCoverage = getAggregateCoverage(
    intl,
    accountGroups,
    ungroupedConnections,
    requiredStatementMonths,
    now
  );
  const coverage = applyAccountsToReview(
    intl,
    aggregateCoverage,
    countGroupsToReview(accountGroups, requiredStatementMonths, now),
    requiredStatementMonths
  );
  const isEveryGapReviewed =
    coverage.kind === "gap" &&
    accountGroups.some((group) => getAccountReviewState(group, requiredStatementMonths, now) === "reviewed") &&
    !hasAccountsToReview(input);
  const months = isEveryGapReviewed ? markGapMonthsReviewed(coverage.months) : coverage.months;

  return isProcessingUploads
    ? { ...coverage, months, body: { kind: "text", text: intl.formatMessage(MESSAGES.aggregateCoverageProcessing) } }
    : { ...coverage, months };
};

export type BankingDataDeclarationChoice =
  | Exclude<FundingApplicationSchemas.BankingDataDeclaration, "full_period_detected">
  | "will_add_missing";

export type NextStepDecision = "proceed" | "record-detected" | "record-cannot-provide" | "ask" | "review-accounts";

export const getNextStepDecision = ({
  hasProvidedBankingData,
  coverageKind,
  hasAccountsToReview,
  hasGroupsToReview,
  declaration,
}: {
  hasProvidedBankingData: boolean;
  coverageKind: AggregateCoverage["kind"];
  hasAccountsToReview: boolean;
  hasGroupsToReview: boolean;
  declaration: FundingApplicationSchemas.BankingDataDeclaration | null | undefined;
}): NextStepDecision => {
  if (!hasProvidedBankingData) {
    return "proceed";
  }
  if (coverageKind === "gap") {
    if (hasAccountsToReview) {
      return "ask";
    }
    return declaration === "cannot_provide" ? "proceed" : "record-cannot-provide";
  }
  if (hasGroupsToReview) {
    return "review-accounts";
  }
  return declaration === "full_period_detected" ? "proceed" : "record-detected";
};

export const getPreviousDeclarationChoice = (
  declaration: FundingApplicationSchemas.BankingDataDeclaration | null | undefined
): BankingDataDeclarationChoice | null =>
  declaration === "full_period_confirmed" || declaration === "cannot_provide" ? declaration : null;

export type StatementCardState = "normal" | "refused";

export const getStatementCardState = ({
  extractionState,
  extractionFailureReason,
}: {
  extractionState: string | null;
  extractionFailureReason: string | null;
}): StatementCardState =>
  extractionState === "failed" && extractionFailureReason === "model_refused" ? "refused" : "normal";
